/**
 * MFE Panel Component
 *
 * A container panel for displaying an MFE in the home dashboard grid.
 * Uses the same MFELoader component as full-view routes so the load path is identical.
 *
 * @module components/home/MFEPanel
 */

import React, { useState, useCallback, Component, ErrorInfo, ReactNode } from 'react';
import { MFELoader as MFELoaderComponent } from '@/components/mfe/MFELoader';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import RefreshIcon from '@mui/icons-material/Refresh';
import './MFEPanel.css';

// ==================== Error Boundary ====================

interface ErrorBoundaryProps {
  children: ReactNode;
  mfeName: string;
  onRetry: () => void;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class MFEErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check if this is a network/CORS error from within the MFE
    const errorMessage = error?.message || '';
    const isNetworkError =
      errorMessage.includes('Failed to fetch') ||
      errorMessage.includes('storage.googleapis.com') ||
      errorMessage.includes('CORS') ||
      errorMessage.includes('NetworkError');

    if (isNetworkError) {
      console.warn(`[MFEPanel] ${this.props.mfeName} network error (likely CORS):`, errorMessage);
    } else {
      console.error(`[MFEPanel] ${this.props.mfeName} crashed:`, error, errorInfo);
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="mfe-panel-error">
          <p>Something went wrong</p>
          <button
            className="mfe-panel-retry-btn"
            onClick={() => {
              this.setState({ hasError: false, error: null });
              this.props.onRetry();
            }}
          >
            <RefreshIcon fontSize="small" />
            Retry
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

// ==================== MFE Panel Component ====================

interface MFEPanelProps {
  mfeName: string;
  title: string;
  iconSrc: string;
  accessToken: string;
  refUserId: string;
  onExpand: () => void;
  /** Optional initial path for router-based MFEs (e.g. Community needs "/" to show feed when host URL is /home) */
  initialPath?: string;
  /** Show red-dot notification badge (from nav-badges API) */
  showBadge?: boolean;
}

export const MFEPanel: React.FC<MFEPanelProps> = ({
  mfeName,
  title,
  iconSrc,
  accessToken,
  refUserId,
  onExpand,
  initialPath,
  showBadge = false,
}) => {
  const [retryKey, setRetryKey] = useState(0);

  const handleLogout = useCallback(() => {
    console.log(`[MFEPanel] ${mfeName} requested logout`);
  }, [mfeName]);

  const handleRefresh = useCallback(() => {
    console.log(`[MFEPanel] Refreshing ${mfeName}...`);
    setRetryKey(prev => prev + 1);
  }, [mfeName]);

  return (
    <div className="mfe-panel">
      <div className="mfe-panel-header">
        <div className="mfe-panel-title-area">
          <span className="mfe-panel-icon-wrap">
            <img src={iconSrc} alt={title} className="mfe-panel-icon" />
            {showBadge && <span className="mfe-panel-badge" aria-hidden />}
          </span>
          <h3 className="mfe-panel-title">{title}</h3>
        </div>
        <div className="mfe-panel-actions">
          <button
            type="button"
            className="mfe-panel-action-btn"
            onClick={handleRefresh}
            title={`Refresh ${title}`}
            aria-label={`Refresh ${title}`}
          >
            <RefreshIcon fontSize="small" />
          </button>
          <button
            type="button"
            className="mfe-panel-action-btn"
            onClick={onExpand}
            title={`Open ${title} in full view`}
            aria-label={`Expand ${title}`}
          >
            <OpenInFullIcon fontSize="small" />
          </button>
        </div>
      </div>

      <div className="mfe-panel-content">
        {mfeName === 'Community' ? (
          /* Community MFE's router only matches when URL is /community. On home the host URL is /home
             so the MFE renders nothing. Load /community in an iframe so it gets the correct URL. */
          <div key={retryKey} className="mfe-panel-iframe-wrapper">
            <iframe
              title={title}
              src="/community?embed=1"
              className="mfe-panel-iframe-el mfe-panel-iframe-el--community"
            />
          </div>
        ) : mfeName === 'Rewards' ? (
          /* Rewards MFE throws "Script error." when refreshed via MFELoader (cache clear + re-import).
             Load in an iframe so Refresh just remounts the iframe; no re-execution in host context.
             index.html runs redirect script when embed=1 so flame → /streaks goes to top window. */
          <div key={retryKey} className="mfe-panel-iframe-wrapper">
            <iframe
              title={title}
              src="/rewards?embed=1"
              className="mfe-panel-iframe-el mfe-panel-iframe-el--rewards"
            />
          </div>
        ) : (
          <MFEErrorBoundary
            key={retryKey}
            mfeName={mfeName}
            onRetry={handleRefresh}
          >
            <MFELoaderComponent
              key={retryKey}
              mfeName={mfeName}
              accessToken={accessToken}
              refUserId={String(refUserId)}
              onLogout={handleLogout}
              onExpandToFullView={onExpand}
              initialPath={initialPath}
              reloadTrigger={retryKey}
            />
          </MFEErrorBoundary>
        )}
      </div>
    </div>
  );
};

